import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { ButtonContainer, MOBILE_STICKY_BOTTOM_INSET_PX } from '../../components/ManageCollection/ManageCollection.styles';
import { routes } from '../../constants.js/misc';
import {
    ALLOWED_PRICE_STRATEGIES,
    LEGACY_PRICE_STRATEGY_LABELS,
    PRICE_STRATEGIES,
    STOREFRONT_PROMOTIONS_ENABLED,
    isPromotionsFeatureEnabled,
} from '../../constants/promotionConfig';
import {
    RULE_TYPES,
    TIER_SCOPE_TYPES,
    createEmptyTier,
    mapTierTargetsToSelection,
    validateTierForm,
    thresholdsToTiers,
    tiersToThresholds,
    buildTierUpdatePayload,
} from '../../constants/pricingTierConfig';
import {
    createPricingTierService,
    enrichTierProductTargets,
    getPricingTierService,
    parseTierIdFromResponse,
    setPricingTierTargetsService,
    updatePricingTierService,
} from '../../services/pricingTiers';
import { getCollectionsService } from '../../services/collections';
import {
    applyPromotionProductsService,
    createPromotionService,
    getPromotionService,
    parsePromotionIdFromResponse,
    pollPromotionUntilSettled,
    updatePromotionService,
} from '../../services/promotions';
import PromotionScopeSection from './components/PromotionScopeSection';
import PromotionSidebar, { PromotionStatusPanel } from './components/PromotionSummaryPanel';
import PromotionStrategyExample, {
    PromotionStrategyGlossary,
} from './components/PromotionStrategyExample';
import PricingTiersSection from './components/PricingTiersSection';
import PromotionsBestOfNotice from './components/PromotionsBestOfNotice';
import LimitedFreemiumPlan from '../../components/LimitedFreemiumPlan';

const emptyForm = {
    rule_type: STOREFRONT_PROMOTIONS_ENABLED ? 'promotion' : 'quantity_discount',
    name: '',
    discount_type: 'percentage',
    discount_value: '',
    price_strategy: 'current_price',
    active: true,
    useSchedule: false,
    starts_at: '',
    ends_at: '',
    targetType: 'collection',
    scope: 'collection',
    tiers: [createEmptyTier()],
    selectedCollections: [],
    selectedProducts: [],
};

const toDateInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toIsoStart = (dateStr) => {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).toISOString();
};

const toIsoEnd = (dateStr) => {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59`).toISOString();
};

const SectionCard = ({ title, subtitle, children, action, compact }) => (
    <Paper
        elevation={0}
        style={{
            padding: compact ? 14 : 20,
            marginBottom: compact ? 12 : 16,
            borderRadius: 12,
            border: '1px solid #E8E8E8',
        }}
    >
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={subtitle ? (compact ? 0.75 : 1) : compact ? 1.5 : 2}
        >
            <Box pr={action ? 1 : 0}>
                <Typography
                    variant={compact ? 'body1' : 'subtitle1'}
                    style={{ fontWeight: 600, fontSize: compact ? 15 : undefined }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 4, lineHeight: 1.45, fontSize: compact ? 13 : undefined }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
            {action}
        </Box>
        {children}
    </Paper>
);

const PromotionFormPage = ({ storeId, storeData }) => {
    const history = useHistory();
    const location = useLocation();
    const { id: promotionId } = useParams();
    const isTierEdit = location.pathname.includes('/promotions/tier/edit');
    const isPromotionEdit = Boolean(promotionId) && !isTierEdit;
    const isEdit = Boolean(promotionId);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [form, setForm] = useState(emptyForm);
    const [collectionsCatalog, setCollectionsCatalog] = useState([]);
    const [loading, setLoading] = useState(isEdit && (isTierEdit || STOREFRONT_PROMOTIONS_ENABLED));
    const [saving, setSaving] = useState(false);
    const [showStrategyExample, setShowStrategyExample] = useState(true);
    const [loadedProductCount, setLoadedProductCount] = useState(0);
    const [resolvingProductNames, setResolvingProductNames] = useState(false);

    const storeCurrency = storeData?.currency || storeData?.store_plan?.currency;
    const promotionsFeatureEnabled = isPromotionsFeatureEnabled(storeData);
    const isQuantityRule = form.rule_type === 'quantity_discount';

    const strategyMeta = PRICE_STRATEGIES.find((s) => s.id === form.price_strategy);

    const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const parseCollectionsCatalog = (res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.collections || [];
        return list.filter((c) => !c.default);
    };

    const normalizeTierScope = (scope) => {
        if (scope === 'products') return 'product';
        if (TIER_SCOPE_TYPES.some((t) => t.id === scope)) return scope;
        return 'store';
    };

    const applyTierDataToForm = useCallback((data, catalog, selection = {}) => {
        const thresholds = Array.isArray(data.thresholds) ? data.thresholds : [];
        const targets = Array.isArray(data.targets) ? data.targets : [];
        const mapped = mapTierTargetsToSelection(targets, catalog);

        setForm({
            ...emptyForm,
            rule_type: 'quantity_discount',
            name: data.name || '',
            discount_type: data.discount_type || 'percentage',
            active: Boolean(data.active),
            scope: normalizeTierScope(data.scope),
            useSchedule: Boolean(data.starts_at || data.ends_at),
            starts_at: toDateInputValue(data.starts_at),
            ends_at: toDateInputValue(data.ends_at),
            tiers: thresholdsToTiers(thresholds),
            selectedCollections: selection.selectedCollections ?? mapped.selectedCollections,
            selectedProducts: selection.selectedProducts ?? mapped.selectedProducts,
        });
    }, []);

    const loadCollections = useCallback(() => {
        if (!storeId || !promotionsFeatureEnabled) return;
        getCollectionsService(storeId)
            .then((res) => {
                setCollectionsCatalog(parseCollectionsCatalog(res));
            })
            .catch(() => {
                enqueueSnackbar('No se pudieron cargar las categorías', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });
    }, [storeId, enqueueSnackbar, promotionsFeatureEnabled]);

    useEffect(() => {
        loadCollections();
    }, [loadCollections]);

    useEffect(() => {
        if (STOREFRONT_PROMOTIONS_ENABLED) return;
        if (isPromotionEdit) {
            history.replace(routes.promotionsPage);
            return;
        }
        if (location.pathname === routes.promotionsCreate) {
            history.replace(routes.promotionsCreateTier);
        }
    }, [isPromotionEdit, history, location.pathname]);

    useEffect(() => {
        if (isEdit) return;
        if (!STOREFRONT_PROMOTIONS_ENABLED) {
            patchForm({ rule_type: 'quantity_discount' });
            return;
        }
        const params = new URLSearchParams(location.search);
        if (params.get('rule') === 'quantity_discount') {
            patchForm({ rule_type: 'quantity_discount' });
        }
    }, [isEdit, location.search]);

    useEffect(() => {
        if (!promotionsFeatureEnabled || !isTierEdit || !storeId || !promotionId) return;
        let cancelled = false;
        setLoading(true);
        setResolvingProductNames(false);

        Promise.all([
            getCollectionsService(storeId).catch(() => ({ data: [] })),
            getPricingTierService(storeId, promotionId),
        ])
            .then(async ([collectionsRes, tierRes]) => {
                if (cancelled) return;
                const catalog = parseCollectionsCatalog(collectionsRes);
                const data = tierRes?.data;
                if (!data) {
                    throw new Error('NO_TIER_DATA');
                }
                setCollectionsCatalog(catalog);

                const mapped = mapTierTargetsToSelection(
                    Array.isArray(data.targets) ? data.targets : [],
                    catalog
                );
                let selectedProducts = mapped.selectedProducts;

                if (normalizeTierScope(data.scope) === 'product' && selectedProducts.length > 0) {
                    setResolvingProductNames(true);
                    selectedProducts = await enrichTierProductTargets(storeId, selectedProducts);
                    if (cancelled) return;
                    setResolvingProductNames(false);
                }

                applyTierDataToForm(data, catalog, {
                    selectedCollections: mapped.selectedCollections,
                    selectedProducts,
                });
            })
            .catch(() => {
                if (cancelled) return;
                enqueueSnackbar('No se pudo cargar la regla de precio por cantidad', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
                history.push(routes.promotionsPage);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setResolvingProductNames(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isTierEdit, storeId, promotionId, enqueueSnackbar, history, applyTierDataToForm, promotionsFeatureEnabled]);

    useEffect(() => {
        if (
            !promotionsFeatureEnabled ||
            !STOREFRONT_PROMOTIONS_ENABLED ||
            !isEdit ||
            isTierEdit ||
            !storeId ||
            !promotionId
        ) {
            return;
        }
        setLoading(true);
        getPromotionService(storeId, promotionId)
            .then(({ data }) => {
                setForm({
                    ...emptyForm,
                    name: data.name || '',
                    discount_type: data.discount_type || 'percentage',
                    discount_value: String(data.discount_value ?? ''),
                    price_strategy: data.price_strategy || 'current_price',
                    active: Boolean(data.active),
                    useSchedule: Boolean(data.starts_at || data.ends_at),
                    starts_at: toDateInputValue(data.starts_at),
                    ends_at: toDateInputValue(data.ends_at),
                });
                setLoadedProductCount(data.product_count ?? 0);
            })
            .catch(() => {
                enqueueSnackbar('No se pudo cargar la promoción', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
                history.push(routes.promotionsPage);
            })
            .finally(() => setLoading(false));
    }, [isEdit, storeId, promotionId, enqueueSnackbar, history, promotionsFeatureEnabled, isTierEdit]);

    const addCollection = (col) => {
        const sid = String(col.id);
        if (form.selectedCollections.some((c) => String(c.id) === sid)) return;
        patchForm({
            selectedCollections: [
                ...form.selectedCollections,
                { id: col.id, name: col.name, picture_url: col.picture_url },
            ],
        });
    };

    const removeCollection = (id) => {
        patchForm({
            selectedCollections: form.selectedCollections.filter((c) => String(c.id) !== String(id)),
        });
    };

    const addProduct = (product) => {
        const sid = String(product.id);
        if (form.selectedProducts.some((p) => String(p.id) === sid)) return;
        patchForm({
            selectedProducts: [
                ...form.selectedProducts,
                {
                    id: product.id,
                    name: product.name,
                    sku: product.sku,
                    picture_url: product.picture_url,
                    images: product.images,
                },
            ],
        });
    };

    const removeProduct = (id) => {
        patchForm({
            selectedProducts: form.selectedProducts.filter((p) => String(p.id) !== String(id)),
        });
    };

    const hasTargetingSelection =
        form.selectedProducts.length > 0 || form.selectedCollections.length > 0;

    const handleTierChange = (localId, patch) => {
        patchForm({
            tiers: form.tiers.map((t) => (t.localId === localId ? { ...t, ...patch } : t)),
        });
    };

    const handleAddTier = () => {
        setForm((prev) => {
            if (prev.tiers.length >= 10) return prev;
            return {
                ...prev,
                tiers: [...prev.tiers, createEmptyTier()],
            };
        });
    };

    const handleRemoveTier = (localId) => {
        setForm((prev) => {
            if (prev.tiers.length <= 1) return prev;
            return {
                ...prev,
                tiers: prev.tiers.filter((t) => t.localId !== localId),
            };
        });
    };

    const tierFormValidation = useMemo(
        () => validateTierForm(form.tiers, form.discount_type),
        [form.tiers, form.discount_type]
    );

    const validateTier = () => {
        if (!(form.name || '').trim()) {
            enqueueSnackbar('Ingresa un nombre para la regla', { variant: 'warning' });
            return false;
        }
        const tierCheck = validateTierForm(form.tiers, form.discount_type);
        if (!tierCheck.valid) {
            enqueueSnackbar(tierCheck.message, { variant: 'warning' });
            return false;
        }
        const thresholds = tiersToThresholds(form.tiers);
        if (thresholds.length === 0) {
            enqueueSnackbar('Agrega al menos un nivel de precio', { variant: 'warning' });
            return false;
        }
        if (form.scope === 'collection' && form.selectedCollections.length === 0) {
            enqueueSnackbar('Agrega al menos una categoría', { variant: 'warning' });
            return false;
        }
        if (form.scope === 'product' && form.selectedProducts.length === 0) {
            enqueueSnackbar('Agrega al menos un producto', { variant: 'warning' });
            return false;
        }
        if (form.useSchedule) {
            if (!form.starts_at || !form.ends_at) {
                enqueueSnackbar('Completa fecha de inicio y fin', { variant: 'warning' });
                return false;
            }
            if (form.starts_at > form.ends_at) {
                enqueueSnackbar('La fecha de fin debe ser posterior al inicio', { variant: 'warning' });
                return false;
            }
        }
        return true;
    };

    const buildTierPayload = () => {
        const thresholds = tiersToThresholds(form.tiers);
        if (isTierEdit) {
            return buildTierUpdatePayload({
                name: form.name,
                discount_type: form.discount_type,
                thresholds,
                active: form.active,
                useSchedule: form.useSchedule,
                starts_at: form.useSchedule ? toIsoStart(form.starts_at) : null,
                ends_at: form.useSchedule ? toIsoEnd(form.ends_at) : null,
            });
        }
        const payload = {
            name: form.name.trim(),
            scope: form.scope,
            discount_type: form.discount_type,
            thresholds,
            active: Boolean(form.active),
        };
        if (form.useSchedule) {
            payload.starts_at = toIsoStart(form.starts_at);
            payload.ends_at = toIsoEnd(form.ends_at);
        }
        return payload;
    };

    const buildTierTargetsBody = () => {
        if (form.scope === 'collection') {
            return {
                collection_ids: form.selectedCollections.map((c) => String(c.id)),
            };
        }
        if (form.scope === 'product') {
            return {
                product_ids: form.selectedProducts.map((p) => String(p.id)),
            };
        }
        return null;
    };

    const handleSaveTier = async () => {
        if (!validateTier() || !storeId) return;
        setSaving(true);
        let tierId = promotionId;
        const payload = buildTierPayload();

        try {
            if (isTierEdit) {
                await updatePricingTierService(storeId, promotionId, payload);
            } else {
                const { data } = await createPricingTierService(storeId, payload);
                tierId = parseTierIdFromResponse(data);
                if (!tierId) {
                    throw new Error('CREATE_NO_ID');
                }
            }
        } catch (err) {
            const apiMsg = err?.response?.data?.message;
            enqueueSnackbar(
                typeof apiMsg === 'string' && apiMsg.trim()
                    ? apiMsg
                    : 'No se pudo guardar la regla de precio por cantidad',
                { variant: 'error', autoHideDuration: 5000 }
            );
            setSaving(false);
            return;
        }

        const targetsBody = buildTierTargetsBody();
        if (targetsBody) {
            try {
                await setPricingTierTargetsService(storeId, tierId, targetsBody);
            } catch (err) {
                const apiMsg = err?.response?.data?.message;
                enqueueSnackbar(
                    typeof apiMsg === 'string' && apiMsg.trim()
                        ? apiMsg
                        : 'La regla se guardó pero no se pudieron asignar categorías o productos',
                    { variant: 'warning', autoHideDuration: 5000 }
                );
            }
        }

        enqueueSnackbar('Regla de precio por cantidad guardada', {
            variant: 'success',
            autoHideDuration: 3500,
        });
        history.push(routes.promotionsPage);
        setSaving(false);
    };

    const validate = () => {
        if (!(form.name || '').trim()) {
            enqueueSnackbar('Ingresa un título para el descuento', { variant: 'warning' });
            return false;
        }
        const value = Number(form.discount_value);
        if (!Number.isFinite(value) || value <= 0) {
            enqueueSnackbar('Ingresa un valor de descuento válido', { variant: 'warning' });
            return false;
        }
        if (form.discount_type === 'percentage' && value > 100) {
            enqueueSnackbar('El porcentaje no puede ser mayor a 100', { variant: 'warning' });
            return false;
        }
        if (!isEdit && !hasTargetingSelection) {
            enqueueSnackbar('Agrega al menos una categoría o un producto', { variant: 'warning' });
            return false;
        }
        if (!ALLOWED_PRICE_STRATEGIES.includes(form.price_strategy)) {
            enqueueSnackbar('Elige cómo se aplicará el descuento en la tienda', { variant: 'warning' });
            return false;
        }
        if (form.useSchedule) {
            if (!form.starts_at || !form.ends_at) {
                enqueueSnackbar('Completa fecha de inicio y fin', { variant: 'warning' });
                return false;
            }
            if (form.starts_at > form.ends_at) {
                enqueueSnackbar('La fecha de fin debe ser posterior al inicio', { variant: 'warning' });
                return false;
            }
        }
        return true;
    };

    const buildPromotionPayload = () => {
        const payload = {
            name: form.name.trim(),
            discount_type: form.discount_type,
            discount_value: Number(Number(form.discount_value).toFixed(4)),
            price_strategy: form.price_strategy,
            active: Boolean(form.active),
        };
        if (form.useSchedule) {
            payload.starts_at = toIsoStart(form.starts_at);
            payload.ends_at = toIsoEnd(form.ends_at);
        }
        return payload;
    };

    const buildApplyBody = () => {
        if (form.selectedProducts.length > 0) {
            return {
                scope: 'product',
                product_ids: form.selectedProducts.map((p) => String(p.id)),
                conflict_resolution: 'overwrite',
            };
        }
        if (form.selectedCollections.length > 0) {
            return {
                scope: 'collection',
                collection_ids: form.selectedCollections.map((c) => String(c.id)),
                conflict_resolution: 'overwrite',
            };
        }
        return { scope: 'existing', conflict_resolution: 'overwrite' };
    };

    const promotionErrorMessage = (err, fallback) => {
        if (err?.message === 'CREATE_NO_ID') {
            return 'La promoción se creó pero el servidor no devolvió el ID. Contacta soporte.';
        }
        if (err?.response?.status === 409) {
            return 'La promoción ya se está aplicando. Espera un momento e intenta de nuevo.';
        }
        if (err?.response?.status >= 500) {
            return fallback;
        }
        const apiMsg = err?.response?.data?.message;
        if (typeof apiMsg === 'string' && apiMsg.trim()) return apiMsg;
        return fallback;
    };

    const handleSave = async () => {
        if (isQuantityRule) {
            await handleSaveTier();
            return;
        }
        if (!validate() || !storeId) return;
        setSaving(true);
        let promoId = promotionId;
        const payload = buildPromotionPayload();

        try {
            if (isEdit) {
                await updatePromotionService(storeId, promotionId, payload);
            } else {
                const { data } = await createPromotionService(storeId, payload);
                promoId = parsePromotionIdFromResponse(data);
                if (!promoId) {
                    throw new Error('CREATE_NO_ID');
                }
            }
        } catch (err) {
            enqueueSnackbar(
                promotionErrorMessage(err, 'No se pudo crear o actualizar la promoción'),
                { variant: 'error', autoHideDuration: 5000 }
            );
            setSaving(false);
            return;
        }

        try {
            await applyPromotionProductsService(storeId, promoId, buildApplyBody());
            const result = await pollPromotionUntilSettled(storeId, promoId);

            if (result.status === 'failed') {
                enqueueSnackbar('La promoción no se pudo aplicar a los productos', {
                    variant: 'error',
                    autoHideDuration: 4000,
                });
            } else if (result.status === 'applied') {
                const skipped = result.skipped_count ?? 0;
                enqueueSnackbar(
                    skipped > 0
                        ? `Promoción aplicada (${result.applied_count} productos, ${skipped} omitidos)`
                        : `Promoción aplicada a ${result.applied_count ?? 0} productos`,
                    { variant: 'success', autoHideDuration: 3500 }
                );
                history.push(routes.promotionsPage);
            } else {
                enqueueSnackbar('Promoción guardada', { variant: 'info', autoHideDuration: 3000 });
                history.push(routes.promotionsPage);
            }
        } catch (err) {
            enqueueSnackbar(
                promotionErrorMessage(err, 'No se pudo aplicar la promoción a los productos'),
                { variant: 'error', autoHideDuration: 5000 }
            );
        } finally {
            setSaving(false);
        }
    };

    const hideRuleTypeOnMobile =
        isMobile && isQuantityRule && !isEdit && STOREFRONT_PROMOTIONS_ENABLED;
    const sectionCompact = isMobile;

    const formColumn = (
        <>
            {isQuantityRule && <PromotionsBestOfNotice compact={sectionCompact} />}
            {!isEdit && STOREFRONT_PROMOTIONS_ENABLED && !hideRuleTypeOnMobile && (
                <SectionCard
                    title="Tipo de regla"
                    subtitle={
                        isMobile
                            ? 'Vitrina o descuento por cantidad en carrito.'
                            : 'Elige si modificas precios en vitrina o descuentos por cantidad en el carrito.'
                    }
                    compact={sectionCompact}
                >
                    <FormControl variant="outlined" fullWidth size={isMobile ? 'small' : 'medium'}>
                        <InputLabel id="rule-type-label">Tipo de regla</InputLabel>
                        <Select
                            labelId="rule-type-label"
                            label="Tipo de regla"
                            value={form.rule_type}
                            onChange={(e) =>
                                patchForm({
                                    rule_type: e.target.value,
                                    ...(e.target.value === 'quantity_discount'
                                        ? { scope: form.scope || 'collection' }
                                        : {}),
                                })
                            }
                        >
                            {RULE_TYPES.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {!isMobile && (
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            {RULE_TYPES.find((t) => t.id === form.rule_type)?.description}
                        </Typography>
                    )}
                </SectionCard>
            )}

            <SectionCard
                title={isQuantityRule ? 'Información general' : 'Título del descuento'}
                subtitle={
                    isQuantityRule
                        ? undefined
                        : isMobile
                          ? 'Solo visible en el panel.'
                          : 'No es visible para tus clientes; solo para identificar la promoción en el panel.'
                }
                compact={sectionCompact}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    label={isQuantityRule ? 'Nombre de la regla' : 'Nombre de la promoción'}
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    placeholder={isQuantityRule ? 'Ej. Mayoreo verano' : 'Ej. Descuento verano'}
                />
            </SectionCard>

            {isQuantityRule && (
                <SectionCard
                    title="Niveles de precio"
                    compact={sectionCompact}
                    action={
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleAddTier}
                            disabled={form.tiers.length >= 10}
                            style={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                            + Agregar nivel
                        </Button>
                    }
                >
                    <PricingTiersSection
                        tiers={form.tiers}
                        discountType={form.discount_type}
                        storeCurrency={storeCurrency}
                        onDiscountTypeChange={(discount_type) => patchForm({ discount_type })}
                        onTierChange={handleTierChange}
                        onRemoveTier={handleRemoveTier}
                    />
                </SectionCard>
            )}

            <SectionCard
                title="Alcance"
                subtitle={isQuantityRule && isMobile ? 'Dónde aplica el descuento por cantidad.' : undefined}
                compact={sectionCompact}
            >
                <PromotionScopeSection
                    compact={isMobile}
                    storeId={storeId}
                    targetType={isQuantityRule ? form.scope : form.targetType}
                    onTargetTypeChange={(value) => {
                        if (isQuantityRule) {
                            patchForm({
                                scope: value,
                                ...(value === 'store'
                                    ? {
                                          selectedProducts: [],
                                          selectedCollections: [],
                                      }
                                    : value === 'collection'
                                      ? { selectedProducts: [] }
                                      : { selectedCollections: [] }),
                            });
                        } else {
                            patchForm({
                                targetType: value,
                                ...(value === 'collection'
                                    ? { selectedProducts: [] }
                                    : { selectedCollections: [] }),
                            });
                        }
                    }}
                    targetTypeOptions={isQuantityRule ? TIER_SCOPE_TYPES : undefined}
                    selectedCollections={form.selectedCollections}
                    selectedProducts={form.selectedProducts}
                    onAddCollection={addCollection}
                    onRemoveCollection={removeCollection}
                    onAddProduct={addProduct}
                    onRemoveProduct={removeProduct}
                    collectionsCatalog={collectionsCatalog}
                    isEdit={isEdit}
                    isTierEdit={isTierEdit}
                    loadedProductCount={loadedProductCount}
                    resolvingProductNames={resolvingProductNames}
                />
            </SectionCard>

            {!isQuantityRule && (
            <SectionCard
                title="Monto del descuento"
                subtitle={
                    isMobile
                        ? form.discount_type === 'fixed'
                            ? `Monto fijo${storeCurrency ? ` (${storeCurrency})` : ''}.`
                            : 'Porcentaje sobre el precio elegido abajo.'
                        : form.discount_type === 'fixed'
                          ? `Monto fijo en la moneda de tu tienda${storeCurrency ? ` (${storeCurrency})` : ''}. La API también admite descuento por porcentaje.`
                          : 'Porcentaje de descuento sobre el precio según la opción elegida abajo.'
                }
                compact={sectionCompact}
            >
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={5}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel id="discount-type-label">Tipo</InputLabel>
                            <Select
                                labelId="discount-type-label"
                                label="Tipo"
                                value={form.discount_type}
                                onChange={(e) => patchForm({ discount_type: e.target.value })}
                            >
                                <MenuItem value="percentage">Porcentaje</MenuItem>
                                <MenuItem value="fixed">Monto fijo</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Valor"
                            value={form.discount_value}
                            onChange={(e) => patchForm({ discount_value: e.target.value })}
                            inputProps={{
                                min: 0,
                                step: form.discount_type === 'percentage' ? 1 : 0.01,
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {form.discount_type === 'percentage'
                                            ? '%'
                                            : storeCurrency || ''}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </SectionCard>
            )}

            {!isQuantityRule && (
            <SectionCard
                title={isMobile ? 'Precio en tienda' : '¿Cómo se verá el precio en la tienda?'}
                subtitle={
                    isMobile
                        ? 'Precio directo o precio tachado con oferta.'
                        : 'Solo baja el precio de venta, o muestra una oferta con precio tachado. Ejemplo con 10% abajo.'
                }
                compact={sectionCompact}
                action={
                    <Button size="small" onClick={() => setShowStrategyExample((v) => !v)}>
                        {showStrategyExample ? 'Ocultar ejemplo' : 'Ver ejemplo'}
                    </Button>
                }
            >
                {!ALLOWED_PRICE_STRATEGIES.includes(form.price_strategy) && (
                    <FormHelperText error style={{ marginBottom: 12 }}>
                        Esta promoción usa una opción antigua (
                        {LEGACY_PRICE_STRATEGY_LABELS[form.price_strategy] || form.price_strategy}
                        ). Elige una de las dos opciones siguientes antes de guardar.
                    </FormHelperText>
                )}
                <PromotionStrategyGlossary />
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
                    {PRICE_STRATEGIES.map((strategy) => (
                        <Button
                            key={strategy.id}
                            variant={form.price_strategy === strategy.id ? 'contained' : 'outlined'}
                            color={form.price_strategy === strategy.id ? 'primary' : 'default'}
                            size="small"
                            onClick={() => patchForm({ price_strategy: strategy.id })}
                            style={{ textTransform: 'none' }}
                        >
                            {strategy.label}
                        </Button>
                    ))}
                </Box>
                {strategyMeta && (
                    <>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                            {strategyMeta.shortHint}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {strategyMeta.summary}
                        </Typography>
                    </>
                )}
                {showStrategyExample && (
                    <PromotionStrategyExample strategyId={form.price_strategy} />
                )}
            </SectionCard>
            )}

            <SectionCard
                title="Vigencia"
                subtitle={
                    isQuantityRule
                        ? isMobile
                            ? 'Opcional: fechas de inicio y fin.'
                            : 'Opcional: limita cuándo aplica el descuento por cantidad en el carrito.'
                        : isMobile
                          ? 'El descuento aplica solo dentro del periodo.'
                          : 'Con fechas definidas, el descuento en precios solo aplica dentro del periodo indicado.'
                }
                compact={sectionCompact}
                action={
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={form.useSchedule}
                                onChange={(e) => patchForm({ useSchedule: e.target.checked })}
                            />
                        }
                        label="Definir fechas"
                        labelPlacement="start"
                        style={{ marginRight: 0 }}
                    />
                }
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Fecha de inicio"
                            type="date"
                            value={form.starts_at}
                            onChange={(e) => patchForm({ starts_at: e.target.value })}
                            disabled={!form.useSchedule}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Fecha de fin"
                            type="date"
                            value={form.ends_at}
                            onChange={(e) => patchForm({ ends_at: e.target.value })}
                            disabled={!form.useSchedule}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                {form.useSchedule && !isMobile && (
                    <FormHelperText>
                        El descuento solo está activo entre la fecha de inicio y la de fin. Antes o
                        después de ese periodo, los productos vuelven a su precio habitual.
                    </FormHelperText>
                )}
            </SectionCard>

            {isMobile && (
                <PromotionSidebar
                    form={form}
                    storeCurrency={storeCurrency}
                    isEdit={isEdit}
                    loadedProductCount={loadedProductCount}
                    isMobile
                    onActiveChange={(active) => patchForm({ active })}
                />
            )}
        </>
    );

    if (!promotionsFeatureEnabled) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                width="100%"
                p={2}
                pb={isMobile ? 10 : 2}
            >
                <Box maxWidth={560} width="100%">
                    <LimitedFreemiumPlan type="payment" embedded />
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
                <CircularProgress />
            </Box>
        );
    }

    const pageTitle = isTierEdit
        ? 'Editar descuento por cantidad'
        : isEdit
          ? 'Editar promoción'
          : isQuantityRule || !STOREFRONT_PROMOTIONS_ENABLED
            ? 'Nuevo descuento por cantidad'
            : 'Nueva promoción';

    return (
        <Box pb={isMobile ? 0 : 4} style={{ paddingBottom: isMobile ? MOBILE_STICKY_BOTTOM_INSET_PX : undefined }}>
            <Grid container spacing={isMobile ? 0 : 3}>
                <Grid item xs={12} style={{ padding: isMobile ? '12px 16px 8px' : '0 0 20px' }}>
                    {isMobile ? (
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18, color: '#1E1E1E' }}>
                                {pageTitle}
                            </Typography>
                            <PromotionStatusPanel
                                active={form.active}
                                onActiveChange={(active) => patchForm({ active })}
                                isMobile
                                inline
                            />
                        </Box>
                    ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                            <Box>
                                <Typography variant="h4" style={{ marginBottom: 10 }}>
                                    {pageTitle}
                                </Typography>
                                <Link component={RouterLink} to={routes.promotionsPage}>
                                    {t('PromotionsTitle')}
                                </Link>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
                                <Button
                                    size="small"
                                    component={RouterLink}
                                    to={routes.promotionsPage}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    style={{ marginLeft: 16 }}
                                    onClick={handleSave}
                                    disabled={saving || (isQuantityRule && !tierFormValidation.valid)}
                                >
                                    {saving
                                        ? 'Guardando…'
                                        : isQuantityRule
                                          ? 'Guardar regla'
                                          : 'Guardar'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {isMobile && (
                        <Link
                            component={RouterLink}
                            to={routes.promotionsPage}
                            style={{ fontSize: 14, display: 'inline-block', marginTop: 8 }}
                        >
                            ‹ {t('PromotionsTitle')}
                        </Link>
                    )}
                </Grid>

                <Grid
                    container
                    spacing={isMobile ? 0 : 3}
                    style={isMobile ? { backgroundColor: '#f5f5f5', paddingBottom: 24 } : undefined}
                >
                    <Grid item xs={12} lg={8}>
                        <Box style={isMobile ? { padding: '0 12px' } : undefined}>{formColumn}</Box>
                    </Grid>

                    {!isMobile && (
                        <Grid item xs={12} lg={4}>
                            <PromotionSidebar
                                form={form}
                                storeCurrency={storeCurrency}
                                isEdit={isEdit}
                                loadedProductCount={loadedProductCount}
                                isMobile={false}
                                onActiveChange={(active) => patchForm({ active })}
                            />
                        </Grid>
                    )}

                    {isMobile && (
                        <ButtonContainer item xs={12}>
                            <Button
                                style={{ width: '100%', padding: '12px 0', fontWeight: 600, fontSize: 15 }}
                                color="primary"
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving || (isQuantityRule && !tierFormValidation.valid)}
                            >
                                {saving
                                    ? 'Guardando…'
                                    : isQuantityRule
                                      ? 'Guardar regla'
                                      : 'Guardar promoción'}
                            </Button>
                        </ButtonContainer>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    storeData: common.storeData,
});

export default connect(mapStateToProps)(PromotionFormPage);
